import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck } from "lucide-react";
import { CodeInput, isCodeReady } from "@/components/MfaWidgets";

export default function AdminLogin() {
  const { login, verifyMfa, enrolMfa, user } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // MFA step state — activated when the backend replies with mfa_required
  const [mfa, setMfa] = useState(null); // { challenge_token, enrolled, user }
  const [enrolData, setEnrolData] = useState(null); // { qr_data_url, shared_secret }
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate(sessionStorage.getItem("volmee_new_recovery_codes") ? "/admin/account" : "/admin", { replace: true }); }, [user, navigate]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (mode === "signin") {
      const res = await login(email, password);
      setLoading(false);
      if (!res.ok) { setError(res.error || "Login failed"); return; }
      if (res.mfa) {
        setMfa(res.mfa);
        // If not enrolled yet, fetch the QR + secret immediately so the
        // super-admin can scan it into their authenticator app.
        if (!res.mfa.enrolled) {
          const en = await enrolMfa(res.mfa.challenge_token);
          if (en.ok) setEnrolData(en.data);
          else setError(en.error || "Could not start MFA enrolment");
        }
        return;
      }
      navigate("/admin");
    } else {
      try {
        const { data } = await api.post("/auth/register", { name, email, password });
        localStorage.setItem("volmee_token", data.access_token);
        window.location.replace("/admin");
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Sign up failed");
      } finally { setLoading(false); }
    }
  }

  async function submitMfa(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await verifyMfa(mfa.challenge_token, code);
    setLoading(false);
    if (!res.ok) { setError(res.error || "MFA verification failed"); return; }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-black/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="font-display text-2xl font-black tracking-tighter">VOLMEE<span className="text-[#FF3B30]">.</span></Link>
          <Link to="/" className="text-sm text-zinc-600 hover:text-black">← Home</Link>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center bg-[#F4F4F5] px-6">
        <div className="w-full max-w-md border border-black/10 bg-white p-8 md:p-10 shadow-[8px_8px_0_0_rgba(0,0,0,0.08)]">
          <div className="text-xs uppercase tracking-widest text-[#002FA7]">Organiser portal</div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tighter">{mfa ? "Verify sign-in." : mode === "signin" ? "Sign in." : "Create account."}</h1>

          {mfa ? (
            <div data-testid="mfa-step" className="mt-6 space-y-5">
              <div className="flex items-start gap-2 border border-[#FFC300]/40 bg-[#FFF9E5] px-3 py-2 text-xs text-[#5A4200]">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#B8860B]" />
                <span>Super Admin account — an additional 6-digit code from your authenticator app is required.</span>
              </div>
              {enrolData ? (
                <div className="border border-black/10 bg-[#F9F9FB] p-4 text-xs" data-testid="mfa-enrol-block">
                  <div className="font-black uppercase tracking-widest text-[#002FA7]">First-time setup</div>
                  <p className="mt-1 text-zinc-700">Scan the QR code with Google Authenticator, Authy or 1Password, then enter the current 6-digit code below.</p>
                  <img src={enrolData.qr_data_url} alt="MFA QR code" className="mt-3 mx-auto h-40 w-40 border border-black/10" data-testid="mfa-qr" />
                  <div className="mt-2 text-center font-mono text-[10px] text-zinc-500">Can't scan? Enter this secret manually:<br /><span className="select-all font-semibold text-black" data-testid="mfa-shared-secret">{enrolData.shared_secret}</span></div>
                </div>
              ) : null}
              <form onSubmit={submitMfa} className="space-y-4">
                <div>
                  <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">6-digit code</span>
                  <CodeInput value={code} onChange={setCode} testId="mfa-code-input" allowRecovery={!enrolData} />
                </div>
                {error ? (<div className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="mfa-error">{error}</div>) : null}
                <button type="submit" disabled={loading || !isCodeReady(code)} data-testid="mfa-submit-btn" className="brand-btn inline-flex w-full items-center justify-center gap-2 px-6 py-3 text-sm font-semibold disabled:opacity-50">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {loading ? "Verifying…" : "Verify & continue"}
                </button>
                <button type="button" onClick={() => { setMfa(null); setEnrolData(null); setCode(""); setError(""); }} className="w-full text-xs text-zinc-600 underline underline-offset-2 hover:text-black">Cancel — back to sign in</button>
              </form>
            </div>
          ) : (
          <>
          <div className="mt-4 inline-flex border border-black/10">
            <button onClick={() => setMode("signin")} data-testid="tab-signin" className={`px-3 py-1.5 text-xs font-semibold ${mode === "signin" ? "bg-black text-white" : "bg-white text-black"}`}>Sign in</button>
            <button onClick={() => setMode("signup")} data-testid="tab-signup" className={`px-3 py-1.5 text-xs font-semibold ${mode === "signup" ? "bg-black text-white" : "bg-white text-black"}`}>Sign up</button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-5">
            {mode === "signup" ? (
              <label className="block">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Name</span>
                <input required value={name} onChange={(e) => setName(e.target.value)} className="admin-input" data-testid="admin-name-input" />
              </label>
            ) : null}
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Email</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="admin-input" data-testid="admin-email-input" />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Password</span>
              <input type="password" required minLength={mode === "signup" ? 6 : 1} value={password} onChange={(e) => setPassword(e.target.value)} className="admin-input" data-testid="admin-password-input" />
            </label>
            {error ? (<div className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="admin-login-error">{error}</div>) : null}
            <button type="submit" disabled={loading} data-testid="admin-login-btn" className="brand-btn inline-flex w-full items-center justify-center gap-2 px-6 py-3 text-sm font-semibold disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
            </button>
          </form>
          </>
          )}
        </div>
      </main>

      <style>{`
        .admin-input { width:100%; border:1px solid rgba(0,0,0,0.15); background:white; padding: 10px 12px; font-size: 14px; border-radius: 2px; outline:none; transition: border-color .15s; }
        .admin-input:focus { border-color: #002FA7; box-shadow: 0 0 0 3px rgba(0,47,167,0.12); }
      `}</style>
    </div>
  );
}
